<template>
  <button
    v-show="isVisible"
    ref="buttonRef"
    type="button"
    class="back-to-top-button show-on-load"
    :class="{ 'back-to-top-button--over-footer': isOverFooter }"
    :aria-label="$t('back-to-top-button')"
    @click="onClick"
  >
    <i class="icon icon-Up" aria-hidden="true"></i>
  </button>
</template>

<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
import useMedia from '../../../utils/useMedia';

const BUTTON_SIZE = 32;
const BASE_OFFSET = 16;
const DIVIDER_GAP = 8;
const DESKTOP_STICKY_CAP = 3;

const { isMedia: isMdUp } = useMedia('(min-width: 768px)');
const { isMedia: isDesktop } = useMedia('(min-width: 1024px)');

let oldOffset = 0;
let movingBottom = true;

const isPageScrollable = ref(false);
const isScrolledVisible = ref(false);
const isOverFooter = ref(false);
const isVisible = computed(
  () => isPageScrollable.value && (isScrolledVisible.value || isOverFooter.value)
);

const siteHeader = ref<HTMLElement | null>(null);
const buttonRef = ref<HTMLButtonElement | null>(null);

let footer: Element | null = null;
let footerBottomSection: Element | null = null;
let footerObserver: IntersectionObserver | null = null;
let isFooterNearViewport = false;
let rafId: number | null = null;
let shouldUpdateScroll = false;

const onClick = () => {
  siteHeader.value?.focus();
};

/**
 * Computes the button's "natural" bottom offset from known layout values.
 */
const getNaturalBottomOffset = (): number => {
  const rootStyle = getComputedStyle(document.documentElement);
  const stickyBooking =
    Number.parseFloat(rootStyle.getPropertyValue('--sticky-booking-block-height')) || 0;
  return (isMdUp.value ? 0 : stickyBooking) + BASE_OFFSET;
};

const updateScrollVisibility = () => {
  movingBottom = window.scrollY > oldOffset;

  if (!movingBottom && window.scrollY > screen.height * 2) {
    isScrolledVisible.value = true;
  }
  if (movingBottom || window.scrollY < screen.height * 2) {
    isScrolledVisible.value = false;
  }

  oldOffset = window.scrollY;
};

const updatePageScrollable = () => {
  isPageScrollable.value =
    document.documentElement.scrollHeight - window.innerHeight > screen.height * 2;
};

const updateFooterPosition = () => {
  if (!isFooterNearViewport || !footer || !buttonRef.value || !isPageScrollable.value) {
    isOverFooter.value = false;
    buttonRef.value?.style.removeProperty('--back-button-sticky-offset');
    return;
  }

  const naturalBottom = getNaturalBottomOffset();
  const footerRect = footer.getBoundingClientRect();
  const buttonCenterY = window.innerHeight - naturalBottom - BUTTON_SIZE / 2;
  isOverFooter.value = footerRect.top <= buttonCenterY;

  if (footerBottomSection) {
    const dividerTop = footerBottomSection.getBoundingClientRect().top;
    const buttonTopY = window.innerHeight - naturalBottom - BUTTON_SIZE;
    const stickyOffset = buttonTopY - dividerTop - DIVIDER_GAP;

    if (stickyOffset > 0) {
      const cappedOffset = isDesktop.value
        ? Math.min(stickyOffset, DESKTOP_STICKY_CAP)
        : stickyOffset;
      buttonRef.value.style.setProperty('--back-button-sticky-offset', `${cappedOffset}px`);
    } else {
      buttonRef.value.style.removeProperty('--back-button-sticky-offset');
    }
  }
};

const syncFooterObserver = () => {
  if (footer?.isConnected) return;

  footerObserver?.disconnect();
  isFooterNearViewport = false;
  footer = document.querySelector('.main-footer');
  footerBottomSection = footer?.querySelector('.main-footer-bottom') ?? null;

  if (!footer) return;

  footerObserver = new IntersectionObserver(
    (entries) => {
      isFooterNearViewport = entries[0]?.isIntersecting ?? false;
      requestUpdate(false);
    },
    { rootMargin: '200px' }
  );
  footerObserver.observe(footer);
};

const requestUpdate = (updateScroll: boolean) => {
  shouldUpdateScroll ||= updateScroll;
  if (rafId !== null) return;

  rafId = requestAnimationFrame(() => {
    rafId = null;
    syncFooterObserver();
    if (shouldUpdateScroll) updateScrollVisibility();
    shouldUpdateScroll = false;
    updatePageScrollable();
    updateFooterPosition();
  });
};

const onScroll = () => requestUpdate(true);
const onResize = () => requestUpdate(false);

onMounted(() => {
  document.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize, { passive: true });

  oldOffset = window.scrollY;
  siteHeader.value = document.getElementById('top');
  syncFooterObserver();
});

onBeforeUnmount(() => {
  document.removeEventListener('scroll', onScroll);
  window.removeEventListener('resize', onResize);
  if (rafId !== null) cancelAnimationFrame(rafId);
  footerObserver?.disconnect();
});
</script>

<style lang="scss">
@import './scss/BackToTop.scss';
</style>
